import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Coffee, Lock, ShieldOff } from 'lucide-react';
import { useLanguage } from '../../features/language/useLanguage';

export type ForbiddenVariant = 'inline' | 'card' | 'page';

interface ForbiddenStateProps {
  variant?: ForbiddenVariant;
  /** Override the localized title. */
  title?: string;
  /** Override the localized description. */
  description?: string;
  /** Extra classes on the outer wrapper. */
  className?: string;
  /** Show the "Back to dashboard" link on the page variant (defaults to true). */
  showBackLink?: boolean;
  /** Optional resource label to display in the eyebrow (page variant only). */
  resourceLabel?: string;
}

/**
 * Rendered when a user hits a layout or query they don't have permission for.
 *
 * A 403 is an expected outcome in a permissioned system, not a failure — this UI
 * leans into the brand's warm amber palette (not an alarming red) so it reads as
 * "this section isn't yours" rather than "something broke".
 */
export const ForbiddenState: React.FC<ForbiddenStateProps> = ({
  variant = 'card',
  title,
  description,
  className,
  showBackLink = true,
  resourceLabel,
}) => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  if (variant === 'inline') {
    return (
      <div
        className={`group flex items-center gap-3 rounded-2xl border border-primary-100 bg-gradient-to-r from-primary-50/70 to-transparent px-4 py-3 ${className ?? ''}`}
        data-test-id="forbidden-state-inline"
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 text-white shadow-sm shadow-primary-500/30 ring-1 ring-primary-300/50">
          <Lock className="h-4 w-4" strokeWidth={2.25} />
        </div>
        <div className="min-w-0">
          <p className="text-[13px] font-semibold tracking-tight text-slate-800">
            {title ?? t('errors.forbidden.inlineTitle')}
          </p>
          <p className="text-xs leading-snug text-slate-500">
            {description ?? t('errors.forbidden.inlineDescription')}
          </p>
        </div>
      </div>
    );
  }

  if (variant === 'card') {
    return (
      <div
        className={`relative overflow-hidden rounded-3xl border border-primary-100/80 bg-gradient-to-br from-white via-primary-50/40 to-white px-6 py-12 text-center ${className ?? ''}`}
        data-test-id="forbidden-state-card"
      >
        {/* Ambient warm glow */}
        <div
          className="pointer-events-none absolute -top-16 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(255,143,0,0.18) 0%, rgba(255,143,0,0) 70%)' }}
          aria-hidden="true"
        />
        <div className="relative flex flex-col items-center">
          <div className="relative">
            <div className="absolute inset-0 rounded-2xl bg-primary-200/40 blur-xl" aria-hidden="true" />
            <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-400 to-primary-600 text-white shadow-lg shadow-primary-500/25 ring-4 ring-primary-100/60">
              <Lock className="h-6 w-6" strokeWidth={2.25} />
            </div>
          </div>
          <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.28em] text-primary-700">
            {t('errors.forbidden.title')}
          </p>
          <p className="mt-2 text-lg font-semibold tracking-tight text-slate-900">
            {title ?? t('errors.forbidden.inlineTitle')}
          </p>
          <p className="mx-auto mt-2 max-w-sm text-[13px] leading-relaxed text-slate-500">
            {description ?? t('errors.forbidden.description')}
          </p>
        </div>
      </div>
    );
  }

  // page variant — the distinctive, production-grade treatment
  return (
    <div
      className={`relative isolate flex min-h-[70vh] items-center justify-center overflow-hidden px-6 py-16 ${className ?? ''}`}
      data-test-id="forbidden-state-page"
    >
      <style>{`
        @keyframes forbiddenRise {
          0%   { opacity: 0; transform: translateY(12px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes forbiddenFade {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes forbiddenPulse {
          0%, 100% { transform: scale(1); opacity: 0.55; }
          50%      { transform: scale(1.06); opacity: 0.75; }
        }
        .fbn-rise { opacity: 0; animation: forbiddenRise 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        .fbn-fade { opacity: 0; animation: forbiddenFade 900ms ease-out forwards; }
        .fbn-pulse { animation: forbiddenPulse 4.5s ease-in-out infinite; }
      `}</style>

      {/* Backdrop: warm radial glow */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(1000px 500px at 50% 25%, rgba(255,143,0,0.10) 0%, rgba(255,143,0,0.02) 40%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      {/* Subtle grid texture */}
      <div
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #6B4423 1px, transparent 1px), linear-gradient(to bottom, #6B4423 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />

      {/* Ghost "403" numeral */}
      <div
        className="fbn-fade pointer-events-none absolute left-1/2 top-1/2 -z-10 -translate-x-1/2 -translate-y-1/2 select-none text-center font-black leading-none tracking-tighter"
        style={{
          fontSize: 'clamp(14rem, 34vw, 28rem)',
          color: 'rgba(255,143,0,0.07)',
          animationDelay: '0ms',
        }}
        aria-hidden="true"
      >
        403
      </div>

      {/* Decorative corner ticks */}
      <CornerTicks className="absolute left-6 top-6 text-primary-300/60" />
      <CornerTicks className="absolute right-6 top-6 rotate-90 text-primary-300/60" />
      <CornerTicks className="absolute bottom-6 left-6 -rotate-90 text-primary-300/60" />
      <CornerTicks className="absolute bottom-6 right-6 rotate-180 text-primary-300/60" />

      <div className="relative mx-auto max-w-lg text-center">
        {/* Badge with pulsing halo */}
        <div className="relative mx-auto flex h-20 w-20 items-center justify-center">
          <div
            className="fbn-pulse absolute inset-0 rounded-[28px] bg-primary-300/60 blur-2xl"
            aria-hidden="true"
          />
          <div
            className="fbn-rise relative flex h-20 w-20 items-center justify-center rounded-[26px] bg-gradient-to-br from-primary-400 via-primary-500 to-primary-700 text-white shadow-[0_12px_40px_-8px_rgba(255,143,0,0.55)] ring-[6px] ring-primary-100/70"
            style={{ animationDelay: '80ms' }}
          >
            <ShieldOff className="h-9 w-9" strokeWidth={2} />
          </div>
        </div>

        {/* Eyebrow with brand flourish */}
        <div
          className="fbn-rise mt-8 flex items-center justify-center gap-3"
          style={{ animationDelay: '180ms' }}
        >
          <span className="h-px w-10 bg-gradient-to-r from-transparent to-primary-400" aria-hidden="true" />
          <Coffee className="h-3 w-3 text-primary-600" strokeWidth={2.5} aria-hidden="true" />
          <span className="text-[10px] font-bold uppercase tracking-[0.32em] text-primary-700">
            {resourceLabel ?? t('errors.forbidden.title')}
          </span>
          <Coffee className="h-3 w-3 text-primary-600" strokeWidth={2.5} aria-hidden="true" />
          <span className="h-px w-10 bg-gradient-to-l from-transparent to-primary-400" aria-hidden="true" />
        </div>

        <h1
          className="fbn-rise mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl"
          style={{ animationDelay: '260ms' }}
        >
          {title ?? t('errors.forbidden.pageTitle')}
        </h1>

        <p
          className="fbn-rise mx-auto mt-4 max-w-md text-[15px] leading-relaxed text-slate-500"
          style={{ animationDelay: '340ms' }}
        >
          {description ?? t('errors.forbidden.pageDescription')}
        </p>

        {showBackLink && (
          <div
            className="fbn-rise mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row"
            style={{ animationDelay: '420ms' }}
          >
            <Link
              to="/"
              className="group inline-flex items-center gap-2 rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-primary-500/30 transition-all duration-200 hover:bg-primary-700 hover:shadow-md hover:shadow-primary-500/40 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              data-test-id="forbidden-back-to-dashboard"
            >
              {t('errors.forbidden.backToDashboard')}
              <span className="h-1.5 w-1.5 rounded-full bg-white/80 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              data-test-id="forbidden-go-back"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2.25} />
              {t('errors.forbidden.goBack')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const CornerTicks: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={`h-8 w-8 ${className ?? ''}`}
    viewBox="0 0 32 32"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.25}
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M2 10 V2 H10" />
    <path d="M6 2 H2 V6" opacity="0.5" />
  </svg>
);
